import { PERMISSIONS } from '@nks/shared';
import { useQuery } from '@tanstack/react-query';
import { Card, Flex, List, Select, Skeleton, Typography } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatDateTime } from '../../lib/format';
import { useHasPermission } from '../auth/session';
import { fetchExceptions, type ExceptionLine, type OperationsMetric } from './api';
import { OperationsRowsDrawer } from './OperationsRowsDrawer';

const { Text } = Typography;

/**
 * Insights → Exceptions (spec 2026-10-29): what is waiting too long in the
 * DMS and what does not match AutoCount, in one list, counted now. A DMS line
 * opens the same rows as Operations; an AutoCount line opens its screen. The
 * 08:00 digest counts exactly this.
 */
export function ExceptionsTab(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canViewAll = useHasPermission(PERMISSIONS.BRANCH_VIEW_ALL);
  const [branch, setBranch] = useState<string | null>(null);
  const [open, setOpen] = useState<{ metric: OperationsMetric; title: string } | null>(null);
  const query = useQuery({ queryKey: ['analytics', 'exceptions', branch], queryFn: () => fetchExceptions(branch) });

  const openLine = (line: ExceptionLine): void => {
    if (line.metric !== null) setOpen({ metric: line.metric, title: t(`insights.exceptions.lines.${line.key}`) });
    else if (line.link !== null) void navigate(line.link);
  };

  const section = (area: ExceptionLine['area'], lines: ExceptionLine[]): ReactNode => (
    <Card size="small" title={t(`insights.exceptions.${area === 'DMS' ? 'dms' : 'autocount'}`)} data-testid={`ex-${area}`}>
      <List
        dataSource={lines}
        renderItem={(line) => (
          <List.Item
            role="button"
            tabIndex={0}
            aria-label={`${t(`insights.exceptions.lines.${line.key}`)}: ${String(line.count)}`}
            style={{ cursor: 'pointer', minHeight: 44 }}
            onClick={() => {
              openLine(line);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openLine(line);
              }
            }}
          >
            <Text>{t(`insights.exceptions.lines.${line.key}`)}</Text>
            <Text strong className="tabular" type={line.count > 0 ? 'warning' : 'secondary'}>
              {line.count.toLocaleString('en-MY')}
            </Text>
          </List.Item>
        )}
      />
    </Card>
  );

  const d = query.data;
  return (
    <Flex vertical gap={12}>
      <Flex justify="space-between" align="center" gap={12} wrap>
        <Text type="secondary">{d === undefined ? '' : t('insights.exceptions.asOf', { at: formatDateTime(d.asOf) })}</Text>
        {canViewAll && (
          <Select<string>
            aria-label={t('insights.operations.branch')}
            value={branch ?? ''}
            onChange={(v) => {
              setBranch(v === '' ? null : v);
            }}
            style={{ minWidth: 200 }}
            options={[
              { value: '', label: t('insights.operations.allBranches') },
              ...(d?.branches ?? []).map((b) => ({ value: b.uid, label: b.name })),
            ]}
          />
        )}
      </Flex>
      {query.isError ? (
        <Text type="danger">{t('insights.loadFailed')}</Text>
      ) : d === undefined ? (
        <Skeleton active />
      ) : (
        <>
          <Text strong data-testid="ex-summary">
            {d.dmsTotal + d.autocountTotal === 0
              ? t('insights.exceptions.clean')
              : t('insights.exceptions.summary', { dms: d.dmsTotal, autocount: d.autocountTotal })}
          </Text>
          {section('DMS', d.lines.filter((l) => l.area === 'DMS'))}
          {section('AUTOCOUNT', d.lines.filter((l) => l.area === 'AUTOCOUNT'))}
        </>
      )}
      <OperationsRowsDrawer
        metric={open?.metric ?? null}
        branch={branch}
        title={open?.title ?? ''}
        onClose={() => {
          setOpen(null);
        }}
      />
    </Flex>
  );
}
